import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Printer, Download, FileText, User, CheckCircle, AlertCircle, Eye, Plus, Edit, Save, Trash2 } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import apiService from '../../utils/api';
import { useToast } from '../../components/Toast';
import { sortedByName } from '../../utils/sortAlpha';
import ConfirmDialog from '../../components/ConfirmDialog';
const TODAY_DATE = new Date().toISOString().split('T')[0];

function BillGeneratorPage() {
  const toast = useToast();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [viewingAllCustomers, setViewingAllCustomers] = useState(false); // view-only: browse all invoices for the selected farm
  const [farmInvoices, setFarmInvoices] = useState([]);
  const [customerSummary, setCustomerSummary] = useState(null);
  const [customerCredit, setCustomerCredit] = useState(0);
  const [unbilledBills, setUnbilledBills] = useState([]);
  const [unbilledPreview, setUnbilledPreview] = useState(null); // { bills, total, count } — all-time unbilled for selected customer
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [pastInvoices, setPastInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // View: 'setup' | 'preview' | 'invoice-preview'
  const [currentView, setCurrentView] = useState('setup');
  const [finalizedInvoice, setFinalizedInvoice] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedBills, setEditedBills] = useState([]); // local edits to invoice's bills

  const billRef = useRef(null);

  const [settings, setSettings] = useState({
    startDate: '',
    endDate: '',
    invoiceDate: new Date().toISOString().split('T')[0]
  });

  const [dairyFarms, setDairyFarms] = useState([]);
  const [selectedFarm, setSelectedFarm] = useState(null);

  // Editable invoice number for the invoice about to be finalized (prefix comes from selectedFarm, read-only)
  const [invoiceNoInput, setInvoiceNoInput] = useState('');
  const [farmNextNumberHint, setFarmNextNumberHint] = useState('');

  useEffect(() => {
    loadCustomers();
    loadDairyFarms();
  }, []);

  // Next invoice number is shared per dairy farm (not per customer) — refresh whenever either changes.
  useEffect(() => {
    if (!selectedCustomer) { setFarmNextNumberHint(''); return; }
    apiService.getNextInvoiceNumber(selectedFarm?.farm_id)
      .then(r => setFarmNextNumberHint(r.data.invoice_number))
      .catch(() => setFarmNextNumberHint(''));
  }, [selectedCustomer, selectedFarm]);

  const loadDairyFarms = async () => {
    try {
      const res = await apiService.getDairyFarms();
      const farms = res.data || [];
      setDairyFarms(farms);
      if (farms.length > 0) setSelectedFarm(farms[0]);
    } catch (err) {
      console.error('Failed to load dairy farms', err);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await apiService.getCustomers();
      setCustomers(response.data || []);
    } catch (err) {
      setError('Failed to load customers: ' + err.message);
    }
  };

  const loadCustomerSummary = async (customerId) => {
    try {
      const response = await apiService.getCustomerSummary(customerId);
      setCustomerSummary(response);
    } catch (err) {
      console.error('Failed to load customer summary', err);
    }
  };

  const loadPastInvoices = async (customerId) => {
    try {
      setLoadingInvoices(true);
      const response = await apiService.getInvoices({ customer_id: customerId });
      setPastInvoices(response.data || []);
    } catch (err) {
      console.error('Failed to load past invoices', err);
      setPastInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const loadUnbilledPreview = async (customerId) => {
    try {
      setLoadingPreview(true);
      const response = await apiService.getUnbilledBills(customerId);
      const bills = response.data || [];
      setUnbilledPreview({
        bills,
        total: response.total_amount || 0,
        count: response.count ?? bills.length,
      });
    } catch (err) {
      console.error('Failed to load unbilled preview', err);
      setUnbilledPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const useUnbilledRange = () => {
    if (!unbilledPreview || unbilledPreview.bills.length === 0) return;
    const dates = unbilledPreview.bills.map(b => b.date).sort();
    setSettings(prev => ({ ...prev, startDate: dates[0], endDate: dates[dates.length - 1] }));
  };

  const loadCustomerCredit = async (customerId) => {
    try {
      const r = await apiService.getCustomerCredit(customerId);
      setCustomerCredit(r.data?.available || 0);
    } catch {
      setCustomerCredit(0);
    }
  };

  const loadFarmInvoices = async (farm) => {
    try {
      setLoadingInvoices(true);
      const response = await apiService.getInvoices(farm ? { dairy_farm_id: farm.farm_id } : {});
      setFarmInvoices(response.data || []);
    } catch (err) {
      console.error('Failed to load farm invoices', err);
      setFarmInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleCustomerSelect = async (e) => {
    const value = e.target.value;
    setUnbilledBills([]);
    setCustomerSummary(null);
    setPastInvoices([]);
    setCustomerCredit(0);
    setUnbilledPreview(null);

    if (value === 'all') {
      setSelectedCustomer(null);
      setViewingAllCustomers(true);
      await loadFarmInvoices(selectedFarm);
      return;
    }

    setViewingAllCustomers(false);
    const customerId = parseInt(value);
    const customer = customers.find(c => c.customer_id === customerId);
    setSelectedCustomer(customer || null);
    if (customer) {
      await Promise.all([
        loadCustomerSummary(customerId),
        loadPastInvoices(customerId),
        loadCustomerCredit(customerId),
        loadUnbilledPreview(customerId),
      ]);
    }
  };

  // Keep the "All Customers" invoice list in sync if the dairy farm changes while viewing it
  useEffect(() => {
    if (viewingAllCustomers) loadFarmInvoices(selectedFarm);
  }, [selectedFarm]);

  const [deleteTarget, setDeleteTarget] = useState(null); // invoice { invoice_id, invoice_number } pending delete confirmation
  const [deletingInvoice, setDeletingInvoice] = useState(false);

  const [editingFarm, setEditingFarm] = useState(false);
  const [farmEditValue, setFarmEditValue] = useState('');
  const [savingFarm, setSavingFarm] = useState(false);

  const handleChangeInvoiceFarm = async () => {
    if (!finalizedInvoice) return;
    try {
      setSavingFarm(true);
      const newFarmId = farmEditValue ? parseInt(farmEditValue) : null;
      await apiService.updateInvoice(finalizedInvoice.invoice_id, { dairy_farm_id: newFarmId });
      const refreshed = await apiService.getInvoice(finalizedInvoice.invoice_id);
      setFinalizedInvoice(refreshed.data);
      toast.success('Dairy farm updated for this invoice.');
      setEditingFarm(false);
    } catch (err) {
      toast.error('Failed to update dairy farm: ' + err.message);
    } finally {
      setSavingFarm(false);
    }
  };

  const [editingInvoiceNo, setEditingInvoiceNo] = useState(false);
  const [invoiceNoEditValue, setInvoiceNoEditValue] = useState('');
  const [savingInvoiceNo, setSavingInvoiceNo] = useState(false);

  const handleChangeInvoiceNo = async () => {
    if (!finalizedInvoice) return;
    const newNo = Number(invoiceNoEditValue);
    if (!newNo || newNo <= 0) { toast.warning('Please enter a valid invoice number.'); return; }
    try {
      setSavingInvoiceNo(true);
      await apiService.updateInvoice(finalizedInvoice.invoice_id, { farm_invoice_no: newNo });
      const refreshed = await apiService.getInvoice(finalizedInvoice.invoice_id);
      setFinalizedInvoice(refreshed.data);
      toast.success('Invoice number updated.');
      setEditingInvoiceNo(false);
      if (selectedCustomer) loadPastInvoices(selectedCustomer.customer_id);
    } catch (err) {
      toast.error('Failed to update invoice number: ' + err.message);
    } finally {
      setSavingInvoiceNo(false);
    }
  };

  const confirmDeleteInvoice = async () => {
    if (!deleteTarget) return;
    try {
      setDeletingInvoice(true);
      await apiService.deleteInvoice(deleteTarget.invoice_id);
      toast.success(`Invoice ${deleteTarget.invoice_number} deleted — its sale entries are unbilled again.`);
      const wasOpen = finalizedInvoice && finalizedInvoice.invoice_id === deleteTarget.invoice_id;
      setDeleteTarget(null);
      if (wasOpen) {
        setFinalizedInvoice(null);
        setCurrentView('setup');
      }
      if (selectedCustomer) {
        await Promise.all([
          loadPastInvoices(selectedCustomer.customer_id),
          loadUnbilledPreview(selectedCustomer.customer_id),
        ]);
      }
    } catch (err) {
      toast.error('Failed to delete invoice: ' + err.message);
    } finally {
      setDeletingInvoice(false);
    }
  };

  const handleViewInvoice = async (invoiceId, openInEdit = false) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getInvoice(invoiceId);
      setFinalizedInvoice(response.data);
      setEditMode(openInEdit);
      setEditingFarm(false);
      setEditingInvoiceNo(false);
      setEditedBills((response.data?.bills || []).map(b => ({
        bill_no: b.bill_no,
        date: b.date,
        morning_cow_milk: b.morning_cow_milk || 0,
        evening_cow_milk: b.evening_cow_milk || 0,
        morning_buffalo_milk: b.morning_buffalo_milk || 0,
        evening_buffalo_milk: b.evening_buffalo_milk || 0,
        cow_milk_price: b.cow_milk_price ?? '',
        buffalo_milk_price: b.buffalo_milk_price ?? '',
        chick_quantity: b.chick_quantity || 0,
        chick_price_per_unit: b.chick_price_per_unit ?? '',
        fatwa_quantity: b.fatwa_quantity || 0,
        fatwa_price_per_unit: b.fatwa_price_per_unit ?? '',
      })));
      setCurrentView('invoice-preview');
    } catch (err) {
      toast.error('Failed to load invoice: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveInvoiceEdits = async () => {
    if (!finalizedInvoice) return;
    try {
      setLoading(true);
      setError(null);
      const payload = {
        bills: editedBills.map(b => ({
          bill_no: b.bill_no,
          date: b.date,
          morning_cow_milk: Number(b.morning_cow_milk) || 0,
          evening_cow_milk: Number(b.evening_cow_milk) || 0,
          morning_buffalo_milk: Number(b.morning_buffalo_milk) || 0,
          evening_buffalo_milk: Number(b.evening_buffalo_milk) || 0,
          cow_milk_price: b.cow_milk_price === '' ? null : Number(b.cow_milk_price),
          buffalo_milk_price: b.buffalo_milk_price === '' ? null : Number(b.buffalo_milk_price),
          chick_quantity: Number(b.chick_quantity) || 0,
          chick_price_per_unit: b.chick_price_per_unit === '' ? null : Number(b.chick_price_per_unit),
          fatwa_quantity: Number(b.fatwa_quantity) || 0,
          fatwa_price_per_unit: b.fatwa_price_per_unit === '' ? null : Number(b.fatwa_price_per_unit),
        }))
      };
      await apiService.updateInvoice(finalizedInvoice.invoice_id, payload);
      // Reload full invoice
      const refreshed = await apiService.getInvoice(finalizedInvoice.invoice_id);
      setFinalizedInvoice(refreshed.data);
      setEditMode(false);
    } catch (err) {
      toast.error('Failed to save invoice edits: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const loadUnbilled = async () => {
    if (!selectedCustomer) { toast.warning('Please select a customer first.'); return; }
    if (!settings.startDate || !settings.endDate) { toast.warning('Please select both start and end dates.'); return; }
    if (new Date(settings.startDate) > new Date(settings.endDate)) { toast.warning('Start date must be before end date.'); return; }

    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getUnbilledBills(selectedCustomer.customer_id, {
        start_date: settings.startDate,
        end_date: settings.endDate
      });
      setUnbilledBills(response.data || []);
      if ((response.data || []).length === 0) {
        setError('No unbilled daily sale records found for this customer in the selected period. All records may already be invoiced.');
        return;
      }
      try {
        const numRes = await apiService.getNextInvoiceNumber(selectedFarm?.farm_id);
        setInvoiceNoInput(String(numRes.data.farm_invoice_no));
      } catch {
        setInvoiceNoInput('1');
      }
      setCurrentView('preview');
    } catch (err) {
      toast.error('Failed to load unbilled records: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (bills) => {
    let cowMilk = 0, buffaloMilk = 0, amount = 0, chick = 0, fatwa = 0, custom = 0;
    (bills || []).forEach(b => {
      cowMilk += (b.morning_cow_milk || 0) + (b.evening_cow_milk || 0);
      buffaloMilk += (b.morning_buffalo_milk || 0) + (b.evening_buffalo_milk || 0);
      amount += b.total_amount || 0;
      chick += (b.chick_quantity || 0) * (b.chick_price_per_unit || 0);
      fatwa += (b.fatwa_quantity || 0) * (b.fatwa_price_per_unit || 0);
      (b.custom_items || []).forEach(ci => { custom += ci.total || 0; });
    });
    return { cowMilk, buffaloMilk, amount, chick, fatwa, custom };
  };

  const handleFinalizeInvoice = async () => {
    if (!selectedCustomer || unbilledBills.length === 0) return;
    if (!invoiceNoInput || Number(invoiceNoInput) <= 0) { toast.warning('Please enter a valid invoice number.'); return; }
    if (!window.confirm(`Finalize invoice for ${unbilledBills.length} daily records? These records will be locked into this invoice and cannot be billed again.`)) return;

    try {
      setLoading(true);
      setError(null);
      const response = await apiService.createInvoice({
        customer_id: selectedCustomer.customer_id,
        invoice_date: settings.invoiceDate,
        bill_ids: unbilledBills.map(b => b.bill_no),
        dairy_farm_id: selectedFarm?.farm_id || null,
        farm_invoice_no: Number(invoiceNoInput),
        notes: ''
      });
      setFinalizedInvoice(response.data);
      setCurrentView('invoice-preview');
      setUnbilledBills([]);
      if (selectedCustomer) loadPastInvoices(selectedCustomer.customer_id);
    } catch (err) {
      toast.error('Failed to finalize invoice: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-IN');
  };

  // Multiple phone numbers are stored comma-separated — show each on its own line instead of "a, b".
  const renderPhones = (phone) => {
    if (!phone) return null;
    return phone.split(',').map(p => p.trim()).filter(Boolean).map((n, i) => (
      <React.Fragment key={i}>
        {i > 0 && <br />}
        {n}
      </React.Fragment>
    ));
  };

  // Display-only: strip the "INV-" prefix (stored value keeps it, for sorting/uniqueness)
  const shortInvoiceNumber = (invoiceNumber) => (invoiceNumber || '').replace(/^INV-/i, '');

  // Sort a list of bills by date ascending and number them 1..N for display —
  // independent of both the global bill_no and the per-owner sr_no.
  const withDisplayNumbers = (bills) => {
    return [...(bills || [])]
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((b, i) => ({ ...b, _displayNo: i + 1 }));
  };

  const downloadPDF = () => {
    const element = billRef.current;
    const inv = finalizedInvoice;
    const fileName = `Invoice_${inv?.invoice_number || 'preview'}_${selectedCustomer?.name}.pdf`;
    html2pdf().set({
      margin: 0.5,
      filename: fileName,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, ignoreElements: (el) => el.classList && el.classList.contains('print-hide') },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] }
    }).from(element).save();
  };

  const printBill = () => window.print();

  const renderLayout = (content) => (
    <div className="app-container">
      <Header />
      <div className="main-content">
        <Sidebar />
        <div className="page-content">{content}</div>
      </div>
    </div>
  );

  // ─── Invoice Preview (after finalize) ────────────────────────────────────────
  if (currentView === 'invoice-preview' && finalizedInvoice) {
    const inv = finalizedInvoice;
    const totals = calculateTotals(inv.bills || []);
    const hasChick = (inv.bills || []).some(b => (b.chick_quantity || 0) > 0) || editedBills.some(e => (Number(e.chick_quantity) || 0) > 0);
    const hasFatwa = (inv.bills || []).some(b => (b.fatwa_quantity || 0) > 0) || editedBills.some(e => (Number(e.fatwa_quantity) || 0) > 0);
    const hasOther = (inv.bills || []).some(b => (b.custom_items || []).length > 0);
    return renderLayout(
      <>
        <div className="page-header print-hide">
          <div className="header-left">
            <button className="btn btn-secondary" onClick={() => {
              setCurrentView('setup');
              setFinalizedInvoice(null);
              if (selectedCustomer) loadPastInvoices(selectedCustomer.customer_id);
            }} style={{ marginRight: '1rem' }}>
              <ArrowLeft size={18} /> Back
            </button>
            <h1 className="page-title">Invoice {shortInvoiceNumber(inv.invoice_number)}</h1>
            <span style={{ marginLeft: '1rem', padding: '4px 12px', borderRadius: '12px', background: 'var(--success-soft)', color: 'var(--success)', fontSize: '0.85rem', fontWeight: 600 }}>
              <CheckCircle size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Finalized
            </span>
          </div>
          <div className="header-right" style={{ display: 'flex', gap: '0.5rem' }}>
            {!editMode && (
              <>
                <button className="btn btn-secondary" onClick={() => setEditMode(true)} disabled={loading}>
                  <Edit size={18} /> Edit
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ color: 'var(--error-color)' }}
                  onClick={() => setDeleteTarget({ invoice_id: inv.invoice_id, invoice_number: inv.invoice_number })}
                  disabled={loading}
                >
                  <Trash2 size={18} /> Delete Invoice
                </button>
              </>
            )}
            {editMode && (
              <>
                <button className="btn btn-secondary" onClick={() => {
                  setEditMode(false);
                  // Reset edits to current invoice
                  setEditedBills((finalizedInvoice?.bills || []).map(b => ({
                    bill_no: b.bill_no, date: b.date,
                    morning_cow_milk: b.morning_cow_milk || 0,
                    evening_cow_milk: b.evening_cow_milk || 0,
                    morning_buffalo_milk: b.morning_buffalo_milk || 0,
                    evening_buffalo_milk: b.evening_buffalo_milk || 0,
                    chick_quantity: b.chick_quantity || 0,
                    chick_price_per_unit: b.chick_price_per_unit ?? '',
                    fatwa_quantity: b.fatwa_quantity || 0,
                    fatwa_price_per_unit: b.fatwa_price_per_unit ?? '',
                  })));
                }} disabled={loading}>Cancel Edit</button>
                <button className="btn btn-primary" onClick={handleSaveInvoiceEdits} disabled={loading}>
                  <Save size={18} /> {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
            <button className="btn btn-secondary" onClick={printBill}><Printer size={18} /> Print</button>
            <button className="btn btn-primary" onClick={downloadPDF}><Download size={18} /> Download PDF</button>
          </div>
        </div>

        <div className="bill-preview" ref={billRef}>
          <div className="bill-container">
            <div className="bill-header">
              {(inv.dairy_farm_fssai_number || selectedFarm?.fssai_number) && (
                <div className="dairy-fssai">FSSAI No: {inv.dairy_farm_fssai_number || selectedFarm?.fssai_number}</div>
              )}
              {(inv.dairy_farm_phone || selectedFarm?.phone) && (
                <div className="dairy-phone">Phone Number: {renderPhones(inv.dairy_farm_phone || selectedFarm?.phone)}</div>
              )}
              <div className="dairy-name">{inv.dairy_farm_name || selectedFarm?.name || '—'}</div>
              {(inv.dairy_farm_description || selectedFarm?.description) && (
                <div className="dairy-description">{inv.dairy_farm_description || selectedFarm?.description}</div>
              )}
              {(inv.dairy_farm_address || selectedFarm?.address) && (
                <div className="dairy-address">{inv.dairy_farm_address || selectedFarm?.address}</div>
              )}
            </div>

            <div className="bill-info" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', margin: '1rem 0', padding: '0.8rem', background: '#f8fafc', borderRadius: '6px' }}>
              <div>
                {!editingInvoiceNo ? (
                  <>
                    <strong>Invoice No:</strong> <span style={{ fontSize: '1.15rem', fontWeight: 700 }}>{shortInvoiceNumber(inv.invoice_number)}</span>{' '}
                    <button
                      className="btn-icon print-hide"
                      style={{ width: '24px', height: '24px' }}
                      onClick={() => { setInvoiceNoEditValue(String(inv.farm_invoice_no)); setEditingInvoiceNo(true); }}
                      title="Change invoice number"
                    >
                      <Edit size={12} />
                    </button>
                  </>
                ) : (
                  <span className="print-hide" style={{ display: 'inline-flex', gap: '0.4rem', alignItems: 'center' }}>
                    <strong>{(inv.invoice_number.includes('-') ? inv.invoice_number.substring(0, inv.invoice_number.lastIndexOf('-')) : 'INV')}-</strong>
                    <input
                      type="number"
                      min="1"
                      className="form-input"
                      style={{ width: '90px', fontSize: '0.85rem', padding: '2px 6px' }}
                      value={invoiceNoEditValue}
                      onChange={e => setInvoiceNoEditValue(e.target.value)}
                    />
                    <button className="btn btn-primary" style={{ padding: '2px 8px', fontSize: '0.75rem' }} onClick={handleChangeInvoiceNo} disabled={savingInvoiceNo}>
                      {savingInvoiceNo ? '...' : 'Save'}
                    </button>
                    <button className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: '0.75rem' }} onClick={() => setEditingInvoiceNo(false)} disabled={savingInvoiceNo}>
                      Cancel
                    </button>
                  </span>
                )}
              </div>
              <div style={{ textAlign: 'right' }}><strong>Invoice Date:</strong> {formatDate(inv.invoice_date)}</div>
              <div><strong>Period:</strong> {formatDate(inv.period_start)} to {formatDate(inv.period_end)}</div>
              <div style={{ textAlign: 'right' }} className="print-hide">
                {!editingFarm ? (
                  <>
                    <strong>Dairy Farm:</strong> {inv.dairy_farm_name || 'Unassigned'}{' '}
                    <button
                      className="btn-icon"
                      style={{ width: '24px', height: '24px' }}
                      onClick={() => { setFarmEditValue(inv.dairy_farm_id ? String(inv.dairy_farm_id) : ''); setEditingFarm(true); }}
                      title="Change dairy farm"
                    >
                      <Edit size={12} />
                    </button>
                  </>
                ) : (
                  <span style={{ display: 'inline-flex', gap: '0.4rem', alignItems: 'center' }}>
                    <select className="form-select" style={{ fontSize: '0.85rem', padding: '2px 6px' }} value={farmEditValue} onChange={e => setFarmEditValue(e.target.value)}>
                      <option value="">Unassigned</option>
                      {sortedByName(dairyFarms).map(f => (
                        <option key={f.farm_id} value={f.farm_id}>{f.name}</option>
                      ))}
                    </select>
                    <button className="btn btn-primary" style={{ padding: '2px 8px', fontSize: '0.75rem' }} onClick={handleChangeInvoiceFarm} disabled={savingFarm}>
                      {savingFarm ? '...' : 'Save'}
                    </button>
                    <button className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: '0.75rem' }} onClick={() => setEditingFarm(false)} disabled={savingFarm}>
                      Cancel
                    </button>
                  </span>
                )}
              </div>
            </div>

            <div className="customer-info" style={{ margin: '0.8rem 0' }}>
              <strong>To: </strong>
              <span style={{ fontWeight: 700 }}>
                {selectedCustomer?.name}{selectedCustomer?.address ? ` (${selectedCustomer.address})` : ''}
              </span>
            </div>

            {(() => {
              const tableClassName = `bill-table ${(hasChick || hasFatwa || hasOther) ? 'bill-table-compact' : ''}`;

              const renderRow = (b) => {
                const ed = editedBills.find(row => row.bill_no === b.bill_no) || {};
                const buffaloQty = (Number(ed.morning_buffalo_milk) || 0) + (Number(ed.evening_buffalo_milk) || 0);
                const cowQty = (Number(ed.morning_cow_milk) || 0) + (Number(ed.evening_cow_milk) || 0);
                const bRate = (b.buffalo_milk_price ?? selectedCustomer?.buffalo_milk_price) || 0;
                const cRate = (b.cow_milk_price ?? selectedCustomer?.cow_milk_price) || 0;
                const liveTotal = buffaloQty * bRate + cowQty * cRate
                  + (Number(ed.chick_quantity) || 0) * (Number(ed.chick_price_per_unit) || 0)
                  + (Number(ed.fatwa_quantity) || 0) * (Number(ed.fatwa_price_per_unit) || 0);
                const setField = (k, v) => setEditedBills(prev => prev.map(row => row.bill_no === b.bill_no ? { ...row, [k]: v } : row));
                return (
                  <tr key={b.bill_no}>
                    <td>{formatDate(b.date)}</td>
                    {editMode ? (
                      <>
                        <td><input type="number" step="0.1" min="0" style={{ width: '60px' }} value={ed.morning_buffalo_milk} onChange={e => setField('morning_buffalo_milk', e.target.value)} /></td>
                        <td><input type="number" step="0.1" min="0" style={{ width: '60px' }} value={ed.evening_buffalo_milk} onChange={e => setField('evening_buffalo_milk', e.target.value)} /></td>
                        <td><input type="number" step="0.1" min="0" style={{ width: '60px' }} value={ed.morning_cow_milk} onChange={e => setField('morning_cow_milk', e.target.value)} /></td>
                        <td><input type="number" step="0.1" min="0" style={{ width: '60px' }} value={ed.evening_cow_milk} onChange={e => setField('evening_cow_milk', e.target.value)} /></td>
                      </>
                    ) : (
                      <>
                        <td>{b.morning_buffalo_milk || '-'}</td>
                        <td>{b.evening_buffalo_milk || '-'}</td>
                        <td>{b.morning_cow_milk || '-'}</td>
                        <td>{b.evening_cow_milk || '-'}</td>
                      </>
                    )}
                    <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>₹{bRate}/L</td>
                    <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>₹{cRate}/L</td>
                    {hasChick && (
                      editMode ? (
                        <>
                          <td><input type="number" step="1" min="0" style={{ width: '55px' }} value={ed.chick_quantity} onChange={e => setField('chick_quantity', e.target.value)} /></td>
                          <td><input type="number" step="0.01" min="0" style={{ width: '60px' }} value={ed.chick_price_per_unit} onChange={e => setField('chick_price_per_unit', e.target.value)} /></td>
                        </>
                      ) : (
                        <>
                          <td>{b.chick_quantity > 0 ? b.chick_quantity : '-'}</td>
                          <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{b.chick_quantity > 0 ? `₹${b.chick_price_per_unit || 0}` : '-'}</td>
                        </>
                      )
                    )}
                    {hasFatwa && (
                      editMode ? (
                        <>
                          <td><input type="number" step="1" min="0" style={{ width: '55px' }} value={ed.fatwa_quantity} onChange={e => setField('fatwa_quantity', e.target.value)} /></td>
                          <td><input type="number" step="0.01" min="0" style={{ width: '60px' }} value={ed.fatwa_price_per_unit} onChange={e => setField('fatwa_price_per_unit', e.target.value)} /></td>
                        </>
                      ) : (
                        <>
                          <td>{b.fatwa_quantity > 0 ? b.fatwa_quantity : '-'}</td>
                          <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{b.fatwa_quantity > 0 ? `₹${b.fatwa_price_per_unit || 0}` : '-'}</td>
                        </>
                      )
                    )}
                    {hasOther && (
                      <td style={{ fontSize: '0.8rem' }}>
                        {(b.custom_items || []).length > 0
                          ? b.custom_items.map(ci => `${ci.item_name} x${ci.quantity}`).join(', ')
                          : '-'}
                      </td>
                    )}
                    <td>₹{(editMode ? liveTotal : (b.total_amount || 0)).toFixed(2)}</td>
                  </tr>
                );
              };

              return (
                <table className={tableClassName}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th colSpan="2">Buffalo (Morn / Night)</th>
                      <th colSpan="2">Cow (Morn / Night)</th>
                      <th>Buffalo Price</th>
                      <th>Cow Price</th>
                      {hasChick && (<><th>Chick Qty</th><th>Chick Price</th></>)}
                      {hasFatwa && (<><th>Fatwa Qty</th><th>Fatwa Price</th></>)}
                      {hasOther && <th>Other Items</th>}
                      <th>Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {withDisplayNumbers(inv.bills).map(renderRow)}
                    <tr className="total-row">
                      <td><strong>TOTAL</strong></td>
                      <td colSpan="2"><strong>{totals.buffaloMilk.toFixed(2)} L</strong></td>
                      <td colSpan="2"><strong>{totals.cowMilk.toFixed(2)} L</strong></td>
                      <td colSpan="2"></td>
                      {hasChick && <td colSpan="2"><strong>₹{totals.chick.toFixed(2)}</strong></td>}
                      {hasFatwa && <td colSpan="2"><strong>₹{totals.fatwa.toFixed(2)}</strong></td>}
                      {hasOther && <td><strong>₹{totals.custom.toFixed(2)}</strong></td>}
                      <td><strong>₹{totals.amount.toFixed(2)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              );
            })()}

            <div className="totals-section">
              <div className="calculation-row">Buffalo: {totals.buffaloMilk.toFixed(2)} L × ₹{selectedCustomer?.buffalo_milk_price || 0} = ₹{(totals.buffaloMilk * (selectedCustomer?.buffalo_milk_price || 0)).toFixed(2)}</div>
              <div className="calculation-row">Cow: {totals.cowMilk.toFixed(2)} L × ₹{selectedCustomer?.cow_milk_price || 0} = ₹{(totals.cowMilk * (selectedCustomer?.cow_milk_price || 0)).toFixed(2)}</div>
              {totals.chick > 0 && <div className="calculation-row">Chick Total: ₹{totals.chick.toFixed(2)}</div>}
              {totals.fatwa > 0 && <div className="calculation-row">Fatwa Total: ₹{totals.fatwa.toFixed(2)}</div>}
              {totals.custom > 0 && <div className="calculation-row">Other Items: ₹{totals.custom.toFixed(2)}</div>}
              <div className="final-total" style={{ fontSize: '1.1rem', fontWeight: 700, borderTop: '2px solid #333', paddingTop: '0.5rem', marginTop: '0.5rem' }}>
                Total Invoice Amount: ₹{(inv.total_amount || 0).toFixed(2)}
              </div>
              <div className="bill-footer-note">
                This is a digitally generated invoice and does not require a physical signature.
              </div>
            </div>
          </div>
        </div>
        <ConfirmDialog
          open={!!deleteTarget}
          title="Delete this invoice?"
          message={`Deleting invoice ${deleteTarget?.invoice_number} will remove it and unbill all its sale entries — they'll be available to invoice again later. This cannot be undone.`}
          confirmLabel="Delete Invoice"
          danger
          loading={deletingInvoice}
          onConfirm={confirmDeleteInvoice}
          onCancel={() => setDeleteTarget(null)}
        />
      </>
    );
  }

  // ─── Unbilled Preview (before finalize) ─────────────────────────────────────
  if (currentView === 'preview' && unbilledBills.length > 0) {
    const totals = calculateTotals(unbilledBills);
    const hasChick = unbilledBills.some(b => (b.chick_quantity || 0) > 0);
    const hasFatwa = unbilledBills.some(b => (b.fatwa_quantity || 0) > 0);
    const hasOther = unbilledBills.some(b => (b.custom_items || []).length > 0);

    return renderLayout(
      <>
        <div className="page-header print-hide">
          <div className="header-left">
            <button className="btn btn-secondary" onClick={() => setCurrentView('setup')} style={{ marginRight: '1rem' }}>
              <ArrowLeft size={18} /> Back
            </button>
            <h1 className="page-title">Invoice Preview — {unbilledBills.length} Records</h1>
          </div>
          <div className="header-right" style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-primary" onClick={handleFinalizeInvoice} disabled={loading}>
              <CheckCircle size={18} /> {loading ? 'Finalizing...' : 'Finalize Invoice'}
            </button>
          </div>
        </div>

        {error && <div className="error-container"><p>{error}</p></div>}

        <div style={{ background: '#fef9c3', border: '1px solid #f59e0b', borderRadius: '8px', padding: '0.8rem 1rem', marginBottom: '1rem', fontSize: '0.9rem' }}>
          <AlertCircle size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle', color: '#d97706' }} />
          Review the unbilled records below. Click <strong>Finalize Invoice</strong> to lock them into a permanent invoice. This cannot be undone.
        </div>

        <div className="form-group print-hide" style={{ maxWidth: '260px', marginBottom: '1rem' }}>
          <label className="form-label">Invoice Number</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>{(selectedFarm?.invoice_prefix || 'INV').toUpperCase()}-</span>
            <input
              type="number"
              min="1"
              className="form-input"
              value={invoiceNoInput}
              onChange={e => setInvoiceNoInput(e.target.value)}
              placeholder="e.g. 46"
            />
          </div>
          <small style={{ color: 'var(--text-secondary)' }}>Auto-suggested from this dairy farm's last invoice (shared across all its customers) — edit if you want to use a different number.</small>
        </div>

        <div className="bill-preview">
          <div className="bill-container">
            <div className="bill-header">
              {selectedFarm?.fssai_number && (
                <div className="dairy-fssai">FSSAI No: {selectedFarm.fssai_number}</div>
              )}
              {selectedFarm?.phone && (
                <div className="dairy-phone">Phone Number: {renderPhones(selectedFarm.phone)}</div>
              )}
              <div className="dairy-name">{selectedFarm?.name || '—'}</div>
              {selectedFarm?.description && (
                <div className="dairy-description">{selectedFarm.description}</div>
              )}
              {selectedFarm?.address && (
                <div className="dairy-address">{selectedFarm.address}</div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', margin: '1rem 0', padding: '0.8rem', background: '#f8fafc', borderRadius: '6px' }}>
              <div><strong>Invoice Date:</strong> {formatDate(settings.invoiceDate)}</div>
              <div><strong>Period:</strong> {formatDate(settings.startDate)} to {formatDate(settings.endDate)}</div>
            </div>

            <div className="customer-info" style={{ margin: '0.8rem 0' }}>
              <strong>To: </strong>
              <span style={{ fontWeight: 700 }}>
                {selectedCustomer?.name}{selectedCustomer?.address ? ` (${selectedCustomer.address})` : ''}
              </span>
            </div>

            <table className={`bill-table ${(hasChick || hasFatwa || hasOther) ? 'bill-table-compact' : ''}`}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th colSpan="2">Buffalo (Morn / Night)</th>
                  <th colSpan="2">Cow (Morn / Night)</th>
                  <th>Buffalo Price</th>
                  <th>Cow Price</th>
                  {hasChick && (<><th>Chick Qty</th><th>Chick Price</th></>)}
                  {hasFatwa && (<><th>Fatwa Qty</th><th>Fatwa Price</th></>)}
                  {hasOther && <th>Other Items</th>}
                  <th>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {withDisplayNumbers(unbilledBills).map((b) => (
                  <tr key={b.bill_no}>
                    <td>{formatDate(b.date)}</td>
                    <td>{b.morning_buffalo_milk || '-'}</td>
                    <td>{b.evening_buffalo_milk || '-'}</td>
                    <td>{b.morning_cow_milk || '-'}</td>
                    <td>{b.evening_cow_milk || '-'}</td>
                    <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>₹{(b.buffalo_milk_price ?? selectedCustomer?.buffalo_milk_price) || 0}/L</td>
                    <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>₹{(b.cow_milk_price ?? selectedCustomer?.cow_milk_price) || 0}/L</td>
                    {hasChick && (
                      <>
                        <td>{b.chick_quantity > 0 ? b.chick_quantity : '-'}</td>
                        <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{b.chick_quantity > 0 ? `₹${b.chick_price_per_unit || 0}` : '-'}</td>
                      </>
                    )}
                    {hasFatwa && (
                      <>
                        <td>{b.fatwa_quantity > 0 ? b.fatwa_quantity : '-'}</td>
                        <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{b.fatwa_quantity > 0 ? `₹${b.fatwa_price_per_unit || 0}` : '-'}</td>
                      </>
                    )}
                    {hasOther && (
                      <td style={{ fontSize: '0.8rem' }}>
                        {(b.custom_items || []).length > 0
                          ? b.custom_items.map(ci => `${ci.item_name} x${ci.quantity}`).join(', ')
                          : '-'}
                      </td>
                    )}
                    <td>₹{(b.total_amount || 0).toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td><strong>TOTAL</strong></td>
                  <td colSpan="2"><strong>{totals.buffaloMilk.toFixed(2)} L</strong></td>
                  <td colSpan="2"><strong>{totals.cowMilk.toFixed(2)} L</strong></td>
                  <td colSpan="2"></td>
                  {hasChick && <td colSpan="2"><strong>₹{totals.chick.toFixed(2)}</strong></td>}
                  {hasFatwa && <td colSpan="2"><strong>₹{totals.fatwa.toFixed(2)}</strong></td>}
                  {hasOther && <td><strong>₹{totals.custom.toFixed(2)}</strong></td>}
                  <td><strong>₹{totals.amount.toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </table>

            <div className="totals-section">
              <div className="calculation-row">Buffalo: {totals.buffaloMilk.toFixed(2)} × ₹{selectedCustomer?.buffalo_milk_price || 0} = ₹{(totals.buffaloMilk * (selectedCustomer?.buffalo_milk_price || 0)).toFixed(2)}</div>
              <div className="calculation-row">Cow: {totals.cowMilk.toFixed(2)} × ₹{selectedCustomer?.cow_milk_price || 0} = ₹{(totals.cowMilk * (selectedCustomer?.cow_milk_price || 0)).toFixed(2)}</div>
              {totals.chick > 0 && <div className="calculation-row">Chick: ₹{totals.chick.toFixed(2)}</div>}
              {totals.fatwa > 0 && <div className="calculation-row">Fatwa: ₹{totals.fatwa.toFixed(2)}</div>}
              <div className="final-total" style={{ fontSize: '1.1rem', fontWeight: 700, borderTop: '2px solid #333', paddingTop: '0.5rem', marginTop: '0.5rem' }}>
                Total: ₹{totals.amount.toFixed(2)}
              </div>
              <div className="bill-footer-note">
                This is a digitally generated invoice and does not require a physical signature.
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ─── Setup View ──────────────────────────────────────────────────────────────
  return renderLayout(
    <>
      <div className="page-header">
        <h1 className="page-title"><FileText size={24} style={{ marginRight: '0.5rem' }} /> Bill Generator</h1>
      </div>

      {error && <div className="error-container"><p>{error}</p></div>}

      <div className="card">
        <div className="card-header" style={{ borderTop: 'none', paddingTop: 0 }}>
          <h3 className="card-title" style={{ fontSize: '1.1rem', color: 'var(--primary-color)' }}>Dairy Information</h3>
        </div>

        <div className="form-group" style={{ maxWidth: '500px' }}>
          <label className="form-label">Select Dairy Farm</label>
          {dairyFarms.length === 0 ? (
            <div style={{ padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              No dairy farms found. <a href="/dairy-farm" style={{ color: 'var(--primary-color)' }}>Add one in Portfolio → Dairy Farm.</a>
            </div>
          ) : (
            <select
              className="form-select"
              value={selectedFarm?.farm_id || ''}
              onChange={e => {
                const farm = dairyFarms.find(f => f.farm_id === parseInt(e.target.value));
                setSelectedFarm(farm || null);
              }}
              disabled={loading}
            >
              <option value="">Select a dairy farm...</option>
              {sortedByName(dairyFarms).map(f => (
                <option key={f.farm_id} value={f.farm_id}>{f.name}</option>
              ))}
            </select>
          )}
          {selectedFarm?.address && (
            <div style={{ marginTop: '0.4rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              {selectedFarm.address}{selectedFarm.phone ? ` · ${selectedFarm.phone}` : ''}
            </div>
          )}
        </div>

        <div className="card-header" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem', marginTop: '1rem' }}>
          <h3 className="card-title" style={{ fontSize: '1.1rem', color: 'var(--primary-color)' }}>Customer Selection</h3>
        </div>

        <div className="form-group" style={{ maxWidth: '500px' }}>
          <label className="form-label">Select Customer</label>
          <select
            className="form-select"
            value={viewingAllCustomers ? 'all' : (selectedCustomer?.customer_id || '')}
            onChange={handleCustomerSelect}
            disabled={loading}
          >
            <option value="">Choose a customer...</option>
            <option value="all">All Customers (view invoices only)</option>
            {sortedByName(customers).map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
          </select>
          {viewingAllCustomers && (
            <small style={{ color: 'var(--text-secondary)' }}>Viewing all invoices for this dairy farm. To create a new bill, select a specific customer above.</small>
          )}
        </div>

        {selectedCustomer && customerCredit > 0.01 && (
          <div style={{ padding: '0.8rem 1rem', background: 'var(--success-bg, #ecfdf5)', borderLeft: '4px solid var(--success-color)', borderRadius: 'var(--radius-md)', marginBottom: '1rem', fontSize: '0.92rem' }}>
            💰 <strong>{selectedCustomer.name}</strong> has <strong>₹{customerCredit.toFixed(2)}</strong> available advance credit. It will auto-apply to the next bill/invoice generated for this customer.
          </div>
        )}

        {customerSummary && (
          <div style={{ background: 'var(--bg-secondary)', padding: '1rem', borderRadius: 'var(--radius-md)', marginBottom: '1.5rem' }}>
            <h4 style={{ marginBottom: '0.8rem', color: 'var(--primary-color)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><User size={16} /> Customer Summary</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.8rem' }}>
              {[
                { label: 'Total Billed', value: `₹${(customerSummary.summary?.total_billed || 0).toFixed(2)}` },
                { label: 'Total Paid', value: `₹${(customerSummary.summary?.total_paid || 0).toFixed(2)}` },
                { label: 'Pending Balance', value: `₹${(customerSummary.summary?.balance || 0).toFixed(2)}`, err: (customerSummary.summary?.balance || 0) > 0 },
                { label: 'Cow Rate', value: `₹${selectedCustomer?.cow_milk_price || 0}/L` },
                { label: 'Buffalo Rate', value: `₹${selectedCustomer?.buffalo_milk_price || 0}/L` },
                { label: 'Payment Terms', value: selectedCustomer?.payment_terms_enabled ? `${selectedCustomer?.payment_terms || 30} days` : 'Not enabled' }
              ].map(({ label, value, err }) => (
                <div key={label}>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{label}</div>
                  <div style={{ fontWeight: 600, color: err ? 'var(--error-color)' : undefined }}>{value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(selectedCustomer || viewingAllCustomers) && (() => {
          const invoiceList = viewingAllCustomers ? farmInvoices : pastInvoices;
          return (
            <>
              <div className="card-header" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem', marginTop: '0.5rem' }}>
                <h3 className="card-title" style={{ fontSize: '1.1rem', color: 'var(--primary-color)' }}>
                  {viewingAllCustomers ? `All Invoices${selectedFarm ? ` — ${selectedFarm.name}` : ''}` : 'Previous Invoices'}
                </h3>
              </div>
              {loadingInvoices ? (
                <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Loading invoices…</div>
              ) : invoiceList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  {viewingAllCustomers ? 'No invoices created yet for this dairy farm.' : 'No invoices created yet for this customer.'}
                </div>
              ) : (
                <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
                  <table className="bill-table">
                    <thead>
                      <tr>
                        <th>Invoice No</th>
                        {viewingAllCustomers && <th>Customer</th>}
                        <th>Invoice Date</th>
                        <th>Period Start</th>
                        <th>Period End</th>
                        <th>Amount (₹)</th>
                        <th>Status</th>
                        <th>View</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceList.map(inv => (
                        <tr key={inv.invoice_id}>
                          <td style={{ fontWeight: 600 }}>{inv.invoice_number}</td>
                          {viewingAllCustomers && <td>{inv.customer_name}</td>}
                          <td>{formatDate(inv.invoice_date)}</td>
                          <td>{formatDate(inv.period_start)}</td>
                          <td>{formatDate(inv.period_end)}</td>
                          <td>₹{(inv.total_amount || 0).toFixed(2)}</td>
                          <td>
                            <span style={{
                              padding: '2px 10px',
                              borderRadius: '10px',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              background: inv.status === 'paid' ? 'var(--success-light)' : inv.status === 'overdue' ? '#fee2e2' : '#fef9c3',
                              color: inv.status === 'paid' ? 'var(--success-color)' : inv.status === 'overdue' ? 'var(--error-color)' : '#b45309',
                            }}>
                              {inv.status}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                className="btn btn-secondary"
                                style={{ padding: '4px 10px', fontSize: '0.7rem' }}
                                onClick={() => handleViewInvoice(inv.invoice_id)}
                                disabled={loading}
                                title="View"
                              >
                                <Eye size={14} />
                              </button>
                              <button
                                className="btn btn-secondary"
                                style={{ padding: '4px 10px', fontSize: '0.7rem' }}
                                onClick={() => handleViewInvoice(inv.invoice_id, true)}
                                disabled={loading}
                                title="Edit"
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                className="btn btn-secondary"
                                style={{ padding: '4px 10px', fontSize: '0.7rem', color: 'var(--error-color)' }}
                                onClick={() => setDeleteTarget({ invoice_id: inv.invoice_id, invoice_number: inv.invoice_number })}
                                disabled={loading}
                                title="Delete Invoice"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          );
        })()}

        {selectedCustomer && (
          <div style={{
            background: unbilledPreview?.count > 0 ? 'var(--warning-soft, #fef3c7)' : 'var(--success-soft, #d1fae5)',
            border: `1px solid ${unbilledPreview?.count > 0 ? 'var(--warning-color, #f59e0b)' : 'var(--success-color, #10b981)'}`,
            borderRadius: '8px', padding: '0.9rem 1.1rem', marginTop: '1rem', marginBottom: '0.5rem'
          }}>
            {loadingPreview ? (
              <span style={{ fontSize: '0.9rem' }}>Checking unbilled sales...</span>
            ) : !unbilledPreview || unbilledPreview.count === 0 ? (
              <span style={{ fontSize: '0.9rem' }}>✅ No unbilled sales for this customer — everything is already invoiced.</span>
            ) : (
              <>
                <div style={{ fontSize: '0.9rem', marginBottom: '0.6rem' }}>
                  <strong>{unbilledPreview.count}</strong> unbilled sale record(s) totaling <strong>₹{unbilledPreview.total.toFixed(2)}</strong>.
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.7rem' }}>
                  {unbilledPreview.bills.map(b => (
                    <span key={b.bill_no} style={{
                      fontSize: '0.78rem', background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(0,0,0,0.08)',
                      borderRadius: '999px', padding: '0.2rem 0.6rem'
                    }}>
                      {formatDate(b.date)}
                    </span>
                  ))}
                </div>
                <button type="button" className="btn btn-secondary" style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }} onClick={useUnbilledRange}>
                  Use this date range ({formatDate(unbilledPreview.bills.map(b => b.date).sort()[0])} — {formatDate(unbilledPreview.bills.map(b => b.date).sort().slice(-1)[0])})
                </button>
              </>
            )}
          </div>
        )}

        {!viewingAllCustomers && (
          <>
            <div className="card-header" style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1.5rem', marginTop: '0.5rem' }}>
              <h3 className="card-title" style={{ fontSize: '1.1rem', color: 'var(--primary-color)' }}>Billing Period</h3>
            </div>

            <div className="form-row" style={{ maxWidth: '700px' }}>
              <div className="form-group">
                <label className="form-label">Period Start Date</label>
                <input type="date" max={TODAY_DATE} className="form-input" value={settings.startDate}
                  onChange={e => handleSettingChange('startDate', e.target.value)} disabled={loading} />
              </div>
              <div className="form-group">
                <label className="form-label">Period End Date</label>
                <input type="date" max={TODAY_DATE} className="form-input" value={settings.endDate}
                  onChange={e => handleSettingChange('endDate', e.target.value)} disabled={loading} />
              </div>
            </div>

            <div className="form-group" style={{ maxWidth: '340px' }}>
              <label className="form-label">Invoice Date (date printed on invoice)</label>
              <input type="date" max={TODAY_DATE} className="form-input" value={settings.invoiceDate}
                onChange={e => handleSettingChange('invoiceDate', e.target.value)} disabled={loading} />
            </div>

            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '0.8rem 1rem', marginBottom: '1rem', fontSize: '0.9rem', color: '#0369a1' }}>
              <strong>How it works:</strong> Only unbilled daily sale records (not yet in any invoice) for the selected period will be shown.
              {selectedCustomer && farmNextNumberHint && (
                <> Once you finalize, they'll be locked into invoice <strong>{farmNextNumberHint}</strong> (this dairy farm's shared numbering) and won't appear again.</>
              )}
            </div>

            <div className="form-actions" style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
              <button className="btn btn-primary btn-lg" style={{ width: '100%' }}
                onClick={loadUnbilled}
                disabled={loading || !selectedCustomer || !settings.startDate || !settings.endDate}>
                <Eye size={18} /> {loading ? 'Loading...' : 'Preview Unbilled Records'}
              </button>
            </div>
          </>
        )}
      </div>
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this invoice?"
        message={`Deleting invoice ${deleteTarget?.invoice_number} will remove it and unbill all its sale entries — they'll be available to invoice again later. This cannot be undone.`}
        confirmLabel="Delete Invoice"
        danger
        loading={deletingInvoice}
        onConfirm={confirmDeleteInvoice}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}

export default BillGeneratorPage;
